import type { ComponentType, FC } from 'react';

/**
 * Props for a component wrapped with defaults: keys present on the defaults object
 * become optional; all other props stay as on `P`.
 */
export type WithDefaultedProps<
  Props extends object,
  DefaultProps extends Partial<Props>,
> = Omit<Props, keyof DefaultProps & keyof Props> &
  Partial<Pick<Props, keyof DefaultProps & keyof Props>>;

const withDefaultProps = <
  Props extends object,
  DefaultProps extends Partial<Props>,
>(
  WrappedComponent: ComponentType<Props>,
  defaultProps: DefaultProps,
): FC<WithDefaultedProps<Props, DefaultProps>> => {
  // Merged props are `Props` at runtime; TS cannot prove this for generic subtype `Props`.
  const WithProps: FC<WithDefaultedProps<Props, DefaultProps>> = (props) => (
    <WrappedComponent
      {...({ ...defaultProps, ...props } as unknown as Props)}
    />
  );

  return WithProps;
};

export default withDefaultProps;
